import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import Table from '../../components/Table';
import Pagination from '../../components/Pagination';
import SearchableClientDropdown from '../../components/SearchableClientDropdown';
import { ColumnDef } from '@tanstack/react-table';
import { 
  projectColumns, 
  PROJECT_JOB_TYPE_OPTIONS,
  PROJECT_WALL_TYPE_OPTIONS,
  PROJECT_ROOF_TYPE_OPTIONS,
  PROJECT_FLOOR_TYPE_OPTIONS,
  PROJECT_STRUCTURE_TYPE_OPTIONS,
  PROJECT_WIND_REGION_OPTIONS,
  PROJECT_SITE_CLASSIFICATION_OPTIONS,
  Project,
  DEFAULT_PAGE_SIZE,
  SEARCH_DEBOUNCE_DELAY
} from './config';
import ProjectColumn from './components/ProjectColumn';
import Dropdown from '../../components/Dropdown';
import { useUserRole } from '../../utils/useUserRole';
import { DropdownItem } from '../../components/Dropdown/config';
import AddJobModal from '../../components/AddJobModal';
import AddStageModal from '../../components/AddStageModal';
import ProjectDetailModal from '../../components/ProjectDetailModal';
import { ProjectDetailTab } from '../../components/ProjectDetailModal/config';
import { useProjectDetailReopen } from '../../components/ProjectDetailModal/useProjectDetailReopen';
import { useServerFiltering } from '../../utils/useServerFiltering';
import { useUrlFilters, PROJECT_FILTER_DEFINITIONS } from '../../utils/useUrlFilters';
import TableSkeleton from '../../components/TableSkeleton';
import { PlusIcon } from '../../components/IconButton/icons';
import ListPage, { FilterTrigger, SearchInput } from '../../components/ListPage';

// =======================================
// INTERFACES
// =======================================

interface TeamMember {
  id: number | string;
  first_name: string;
  last_name: string;
  type?: string;
}

interface WorkflowStatus {
  id: number;
  status_name: string;
  job_types: string[];
}



/** A filter's items: a "none" entry for an unset field, then each option. */
const optionsWithNone = (options: readonly string[], noneLabel: string): DropdownItem[] => [
  { id: 'none', label: noneLabel, value: 'none' },
  ...options.map(option => ({ id: option, label: option, value: option })),
];

const WALL_TYPE_FILTER_ITEMS = optionsWithNone(PROJECT_WALL_TYPE_OPTIONS, 'No Wall Type');
const ROOF_TYPE_FILTER_ITEMS = optionsWithNone(PROJECT_ROOF_TYPE_OPTIONS, 'No Roof Type');
const FLOOR_TYPE_FILTER_ITEMS = optionsWithNone(PROJECT_FLOOR_TYPE_OPTIONS, 'No Floor Type');
const STRUCTURE_TYPE_FILTER_ITEMS = optionsWithNone(PROJECT_STRUCTURE_TYPE_OPTIONS, 'No Structure Type');
const WIND_REGION_FILTER_ITEMS = optionsWithNone(PROJECT_WIND_REGION_OPTIONS, 'No Wind Region');
const SITE_CLASSIFICATION_FILTER_ITEMS = optionsWithNone(PROJECT_SITE_CLASSIFICATION_OPTIONS, 'No Site Classification');

// =======================================
// MAIN COMPONENT
// =======================================

const ProjectRegister: React.FC = () => {
  // Authentication hooks
  const session = useSession();
  const supabase = useSupabaseClient();
  const { isReadOnly } = useUserRole();

  // Static data state (for dropdowns and editing)
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatus[]>([]);
  const [staticDataLoading, setStaticDataLoading] = useState(true);
  const [staticDataError, setStaticDataError] = useState<string | null>(null);

  // UI state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAddStageModalOpen, setIsAddStageModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null);
  const [detailTab, setDetailTab] = useState<ProjectDetailTab | undefined>(undefined);

  // URL-synced filter state - persists across navigation
  const { 
    filters: urlFilters, 
    setFilter,
    setFilters: setUrlFilters,
    resetFilters: resetUrlFilters,
    hasActiveFilters
  } = useUrlFilters(PROJECT_FILTER_DEFINITIONS);

  // Derive filter values from URL state
  const searchInput = urlFilters.search;
  const filterTypes = urlFilters.types;
  const filterStatus = urlFilters.status;
  const filterClient = useMemo(() => 
    urlFilters.client ? [urlFilters.client] : [], 
    [urlFilters.client]
  );
  const filterProjectLead = urlFilters.projectLead;
  const filterWallType = urlFilters.wallType;
  const filterRoofType = urlFilters.roofType;
  const filterFloorType = urlFilters.floorType;
  const filterStructureType = urlFilters.structureType;
  const filterWindRegion = urlFilters.windRegion;
  const filterSiteClassification = urlFilters.siteClassification;
  const urlPage = urlFilters.page;
  const urlPageSize = urlFilters.pageSize;

  // Server-side filtering hook
  const {
    data: projects,
    pagination,
    loading,
    filtering,
    error,
    setFilters,
    refetch
  } = useServerFiltering<Project>({
    rpcFunction: 'get_filtered_projects',
    defaultFilters: {
      search_term: null,
      status_filter: null,
      types_filter: null,
      client_filter: null,
      project_lead_filter: null,
      wall_type_filter: null,
      roof_type_filter: null,
      floor_type_filter: null,
      structure_type_filter: null,
      wind_region_filter: null,
      site_classification_filter: null,
      page_size: DEFAULT_PAGE_SIZE,
      page_offset: 0,
      sort_by: 'job_number',
      sort_order: 'desc'
    },
    debounceDelay: SEARCH_DEBOUNCE_DELAY
  });

  // Sync URL filter state with server filtering
  useEffect(() => {
    const pageSize = urlPageSize || DEFAULT_PAGE_SIZE;
    const pageOffset = ((urlPage || 1) - 1) * pageSize;
    
    setFilters({
      search_term: searchInput || null,
      status_filter: filterStatus.length > 0 ? filterStatus : null,
      types_filter: filterTypes.length > 0 ? filterTypes : null,
      client_filter: filterClient.length > 0 ? filterClient : null,
      project_lead_filter: filterProjectLead.length > 0 ? filterProjectLead : null,
      wall_type_filter: filterWallType.length > 0 ? filterWallType : null,
      roof_type_filter: filterRoofType.length > 0 ? filterRoofType : null,
      floor_type_filter: filterFloorType.length > 0 ? filterFloorType : null,
      structure_type_filter: filterStructureType.length > 0 ? filterStructureType : null,
      wind_region_filter: filterWindRegion.length > 0 ? filterWindRegion : null,
      site_classification_filter: filterSiteClassification.length > 0 ? filterSiteClassification : null,
      page_size: pageSize,
      page_offset: pageOffset
    });
  }, [searchInput, filterStatus, filterTypes, filterClient, filterProjectLead, filterWallType, filterRoofType, filterFloorType, filterStructureType, filterWindRegion, filterSiteClassification, urlPage, urlPageSize, setFilters]);

  // Fetch static data for dropdowns
  useEffect(() => {
    const fetchStaticData = async () => {
      try {
        setStaticDataLoading(true);
        setStaticDataError(null);

        // Fetch active engineers for project lead selection
        const { data: teamData, error: teamError } = await supabase
          .from('active_team_members')
          .select('id, first_name, last_name, type')
          .eq('type', 'Engineer');

        if (teamError) throw teamError;

        // Fetch all workflow statuses with their job types
        const { data: statusData, error: statusError } = await supabase
          .rpc('get_all_workflow_statuses');

        if (statusError) throw statusError;

        setTeamMembers(teamData || []);
        setWorkflowStatuses(statusData || []);

      } catch (err) {
        console.error('Error fetching static data:', err);
        setStaticDataError('Failed to load dropdown data');
      } finally {
        setStaticDataLoading(false);
      }
    };

    if (session) {
      fetchStaticData();
    }
  }, [session, supabase]);

  // Clear invalid status selections when types filter changes
  useEffect(() => {
    if (filterTypes.length > 0 && filterStatus.length > 0) {
      // Get valid statuses for selected types
      const validStatuses = workflowStatuses
        .filter(status => status.job_types.some(jobType => filterTypes.includes(jobType)))
        .map(status => status.status_name);
      
      // Remove any selected statuses that are no longer valid
      const stillValidStatuses = filterStatus.filter(s => validStatuses.includes(s));
      if (stillValidStatuses.length !== filterStatus.length) {
        setFilter('status', stillValidStatuses);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterTypes, workflowStatuses]); // Don't include filterStatus to avoid infinite loop

  // Handler functions
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setUrlFilters({ search: e.target.value, page: 1 });
  }, [setUrlFilters]);

  const handleClearSearch = useCallback(() => {
    setUrlFilters({ search: '', page: 1 });
  }, [setUrlFilters]);

  const handleTypesChange = useCallback((types: string[]) => {
    setUrlFilters({ types, page: 1 });
  }, [setUrlFilters]);

  const handleStatusChange = useCallback((status: string[]) => {
    setUrlFilters({ status, page: 1 });
  }, [setUrlFilters]);

  const handleClientChange = useCallback((clientId: string | number | null) => {
    if (clientId === null || clientId === 'none') {
      setUrlFilters({ client: '', page: 1 });
    } else {
      setUrlFilters({ client: clientId.toString(), page: 1 });
    }
  }, [setUrlFilters]);

  const handleProjectLeadChange = useCallback((leads: string[]) => {
    setUrlFilters({ projectLead: leads, page: 1 });
  }, [setUrlFilters]);

  const handleWallTypeChange = useCallback((types: string[]) => {
    setUrlFilters({ wallType: types, page: 1 });
  }, [setUrlFilters]);

  const handleRoofTypeChange = useCallback((types: string[]) => {
    setUrlFilters({ roofType: types, page: 1 });
  }, [setUrlFilters]);

  const handleFloorTypeChange = useCallback((types: string[]) => {
    setUrlFilters({ floorType: types, page: 1 });
  }, [setUrlFilters]);

  const handleStructureTypeChange = useCallback((types: string[]) => {
    setUrlFilters({ structureType: types, page: 1 });
  }, [setUrlFilters]);

  const handleWindRegionChange = useCallback((regions: string[]) => {
    setUrlFilters({ windRegion: regions, page: 1 });
  }, [setUrlFilters]);

  const handleSiteClassificationChange = useCallback((classifications: string[]) => {
    setUrlFilters({ siteClassification: classifications, page: 1 });
  }, [setUrlFilters]);

  const handleClearFilters = useCallback(() => {
    resetUrlFilters();
  }, [resetUrlFilters]);

  // Pagination handlers - update URL
  const handlePageChange = useCallback((page: number) => {
    setFilter('page', page);
  }, [setFilter]);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setUrlFilters({ pageSize: newPageSize, page: 1 });
  }, [setUrlFilters]);

  // Event handlers
  const handleJobCreated = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleRowClick = useCallback((projectId: number) => {
    setSelectedProjectId(projectId);
    setDetailTab(undefined);
    setIsDetailModalOpen(true);
  }, []);

  // Returning from a document opened out of this modal: put it back, on the tab
  // it was left from. The register only ever opens projects, so the stage half
  // of the instruction has nothing to apply to here.
  useProjectDetailReopen(reopen => {
    setSelectedProjectId(reopen.projectId);
    setDetailTab(reopen.tab);
    setIsDetailModalOpen(true);
  });

  const handleProjectUpdated = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleProjectDeleted = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleDetailModalClose = useCallback(() => {
    setIsDetailModalOpen(false);
    setSelectedProjectId(null);
    setDetailTab(undefined);
  }, []);

  // Create dropdown items - dynamic based on selected job types
  const statusDropdownItems = useMemo<DropdownItem[]>(() => {
    // Filter statuses based on selected job types
    let filteredStatuses = workflowStatuses;
    
    if (filterTypes.length > 0) {
      // Show only statuses that belong to at least one selected job type
      filteredStatuses = workflowStatuses.filter(status =>
        status.job_types.some(jobType => filterTypes.includes(jobType))
      );
    }
    
    return filteredStatuses.map(status => ({
      id: status.status_name,
      label: status.status_name,
      value: status.status_name
    }));
  }, [workflowStatuses, filterTypes]);

  const typesDropdownItems = useMemo<DropdownItem[]>(() => {
    return PROJECT_JOB_TYPE_OPTIONS.map(type => ({
      id: type,
      label: type,
      value: type
    }));
  }, []);

  const projectLeadDropdownItems = useMemo<DropdownItem[]>(() => {
    const items = teamMembers.map(member => ({
      id: member.id.toString(),
      label: `${member.first_name} ${member.last_name}`,
      value: member.id.toString()
    }));
    return [
      { id: 'none', label: 'No Project Lead', value: 'none' },
      ...items
    ];
  }, [teamMembers]);



  // Table columns configuration with flexible width
  const columns = useMemo<ColumnDef<Project>[]>(
    () => {
      // Column budget (table-layout: fixed on desktop; see the Documents
      // register for the reasoning). The fixed columns take ~600px;
      // Description is the one `auto` column and Client a percentage, so
      // the slack goes to Description after Client has its share. Status is
      // sized for "Contract Documentation", the longest pill: nothing clips
      // a cell, so a pill wider than its column draws over the next one.
      const columnConfig: Record<string, { minWidth?: string; width?: string; maxWidth?: string }> = {
        job_number: { minWidth: '96px', width: '96px' },
        created_at: { minWidth: '112px', width: '112px' },
        description: { minWidth: '240px', width: 'auto' },
        client: { minWidth: '180px', width: '20%' },
        types: { minWidth: '104px', width: '104px' },
        status: { minWidth: '196px', width: '196px' },
        project_lead: { minWidth: '96px', width: '96px' }
      };

      // Sort columns by order property
      return Object.entries(projectColumns)
        .sort(([, a], [, b]) => (a.order || 999) - (b.order || 999))
        .map(([key, config]) => ({
          header: config.label || key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
          accessorKey: key,
          meta: columnConfig[key], // Add column sizing metadata
          cell: ({ row }) => (
            <ProjectColumn
              column={key}
              config={config}
              row={row}
            />
          )
        }));
    },
    []
  );

  // Current page from pagination (for display)
  const currentPage = pagination?.page || urlPage || 1;
  const currentPageSize = pagination?.page_size || urlPageSize || DEFAULT_PAGE_SIZE;

  // Error state
  if (staticDataError || error) {
    return (
      <div className="error-container">
        <h3>Error loading project register</h3>
        <p>{staticDataError || error}</p>
        <button onClick={() => window.location.reload()}>Refresh Page</button>
      </div>
    );
  }

  return (
    <ListPage
      title="Project Register"
      className="project-register-page"
      loading={staticDataLoading}
      skeleton={{ segmentCount: 0, filterCount: 8, showAction: true }}
      search={
        <SearchInput
          placeholder="Search by description, job number or keywords…"
          value={searchInput}
          onChange={handleSearchChange}
          onClear={handleClearSearch}
          aria-label="Search projects"
        />
      }
      actions={
        <>
          <button
            type="button"
            className="pill-btn pill-btn--sm"
            onClick={() => setIsAddStageModalOpen(true)}
            disabled={loading || isReadOnly}
          >
            <PlusIcon /> Add stage
          </button>
          <button
            type="button"
            className="pill-btn pill-btn--solid pill-btn--sm"
            onClick={() => setIsAddModalOpen(true)}
            disabled={loading || isReadOnly}
          >
            <PlusIcon /> Add job
          </button>
        </>
      }
      filters={
        <>
          <FilterTrigger label="Type" active={filterTypes.length > 0}>
            <Dropdown
              items={typesDropdownItems}
              value={filterTypes}
              onChange={handleTypesChange}
              placeholder="All"
              multiSelect
              searchable
            />
          </FilterTrigger>
          <FilterTrigger label="Status" active={filterStatus.length > 0}>
            <Dropdown
              items={statusDropdownItems}
              value={filterStatus}
              onChange={handleStatusChange}
              placeholder="All"
              multiSelect
              searchable
            />
          </FilterTrigger>
          <FilterTrigger label="Client" active={filterClient.length > 0}>
            <SearchableClientDropdown
              value={filterClient.length > 0 ? filterClient[0] : null}
              onChange={(clientId: string | number | null) => handleClientChange(clientId)}
              placeholder="All"
              allowNone={true}
              noneLabel="All Clients"
            />
          </FilterTrigger>
          <FilterTrigger label="Lead" active={filterProjectLead.length > 0}>
            <Dropdown
              items={projectLeadDropdownItems}
              value={filterProjectLead}
              onChange={handleProjectLeadChange}
              placeholder="All"
              multiSelect
              searchable
            />
          </FilterTrigger>
          <FilterTrigger label="Structure" active={filterStructureType.length > 0}>
            <Dropdown
              items={STRUCTURE_TYPE_FILTER_ITEMS}
              value={filterStructureType}
              onChange={handleStructureTypeChange}
              placeholder="All"
              multiSelect
              searchable
            />
          </FilterTrigger>
          <FilterTrigger label="Wall" active={filterWallType.length > 0}>
            <Dropdown
              items={WALL_TYPE_FILTER_ITEMS}
              value={filterWallType}
              onChange={handleWallTypeChange}
              placeholder="All"
              multiSelect
              searchable
            />
          </FilterTrigger>
          <FilterTrigger label="Roof" active={filterRoofType.length > 0}>
            <Dropdown
              items={ROOF_TYPE_FILTER_ITEMS}
              value={filterRoofType}
              onChange={handleRoofTypeChange}
              placeholder="All"
              multiSelect
              searchable
            />
          </FilterTrigger>
          <FilterTrigger label="Floor" active={filterFloorType.length > 0}>
            <Dropdown
              items={FLOOR_TYPE_FILTER_ITEMS}
              value={filterFloorType}
              onChange={handleFloorTypeChange}
              placeholder="All"
              multiSelect
              searchable
            />
          </FilterTrigger>
          <FilterTrigger label="Wind" active={filterWindRegion.length > 0}>
            <Dropdown
              items={WIND_REGION_FILTER_ITEMS}
              value={filterWindRegion}
              onChange={handleWindRegionChange}
              placeholder="All"
              multiSelect
              searchable
            />
          </FilterTrigger>
          <FilterTrigger label="Site class" active={filterSiteClassification.length > 0}>
            <Dropdown
              items={SITE_CLASSIFICATION_FILTER_ITEMS}
              value={filterSiteClassification}
              onChange={handleSiteClassificationChange}
              placeholder="All"
              multiSelect
              searchable
            />
          </FilterTrigger>
        </>
      }
      hasActiveFilters={hasActiveFilters}
      onClearFilters={handleClearFilters}
      resultsText={
        !loading && pagination && (
          <>
            Showing {pagination.total_records > 0 ? ((currentPage - 1) * currentPageSize) + 1 : 0}–{Math.min(currentPage * currentPageSize, pagination.total_records)} of {pagination.total_records.toLocaleString()} projects
          </>
        )
      }
      footer={
        <Pagination
          variant="bar"
          currentPage={currentPage}
          totalPages={pagination?.total_pages ?? 1}
          totalRecords={pagination?.total_records ?? 0}
          pageSize={currentPageSize}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          hasNext={pagination?.has_next ?? false}
          hasPrevious={pagination?.has_previous ?? false}
          loading={loading || filtering}
          showPageInfo
          pageSizeOptions={[25, 50, 100]}
        />
      }
    >
      {staticDataLoading || loading ? (
        <TableSkeleton compact rows={14} columns={7} showHeader />
      ) : projects.length === 0 ? (
        <div className="list-page__empty">
          {hasActiveFilters ? 'No projects match these filters.' : 'No projects yet.'}
        </div>
      ) : (
        <div className={`list-page__body ${filtering ? 'is-filtering' : ''}`.trim()}>
          <Table
            data={projects}
            columns={columns}
            onRowClick={(row) => handleRowClick(row.id)}
            stickyHeader
          />
        </div>
      )}

      {/* Modals */}
      <AddJobModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onJobCreated={handleJobCreated}
        teamMembers={teamMembers}
      />

      <AddStageModal
        isOpen={isAddStageModalOpen}
        onClose={() => setIsAddStageModalOpen(false)}
        onStageCreated={handleJobCreated}
        teamMembers={teamMembers}
        workflowType="Design"
      />
      
      <ProjectDetailModal
        isOpen={isDetailModalOpen}
        projectId={selectedProjectId}
        initialTab={detailTab}
        onClose={handleDetailModalClose}
        onProjectUpdated={handleProjectUpdated}
        onProjectDeleted={handleProjectDeleted}
        teamMembers={teamMembers}
      />
    </ListPage>
  );
};

export default ProjectRegister;
